import type { INotice, Notice as APINotice } from 'oscar-typedef'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import type { Maybe } from '@/types'
import { getExecuteOnce } from '@/utils'

const useNoticeAPIStore = defineStore('noticeAPI', () => {
  const noticeList = ref<Maybe<INotice[]>>()

  const fetchNoticeList = getExecuteOnce(
    async (
      force: boolean | undefined = false,
      params?: APINotice.GetNoticeList.Request.Queries,
    ) => {
      if (force || !noticeList.value) {
        const res =
          await WepinBackendAPI.getInstance().notice.getNoticeList(params)
        if (!res) return
        if (!noticeList.value) noticeList.value = []
        noticeList.value = noticeList.value.concat(res.notices)
      }
      return noticeList.value
    },
  )

  const fetchNotice = getExecuteOnce(
    async (noticeId: INotice['id'], force: boolean | undefined = false) => {
      if (!noticeId) return
      if (force || !noticeList.value) {
        const res = await WepinBackendAPI.getInstance().notice.getNotice({
          noticeId,
        })
        if (!res) return
        noticeList.value = noticeList.value?.map((notice) =>
          notice.id === noticeId ? res.notice : notice,
        )
        return res.notice
      }
      return noticeList.value.find((notice) => notice.id === noticeId)
    },
  )

  const deleteNotice = async (noticeId: INotice['id']) => {
    if (!noticeId) return
    const res = await WepinBackendAPI.getInstance().notice.deleteNotice({
      noticeId,
    })
    if (res.result) {
      noticeList.value = noticeList.value?.filter(
        (notice) => notice.id !== noticeId,
      )
    }
    return res
  }

  const reset = () => {
    noticeList.value = []
  }

  return {
    noticeList,
    fetchNoticeList,
    fetchNotice,
    deleteNotice,
    reset,
  }
})

export { useNoticeAPIStore }
